/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 31/08/2025 - 13:51:32
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 31/08/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
  Analytics as AnalyticsIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  AccountCircle as AccountCircleIcon,
  CheckCircleOutline as CheckCircleIcon,
  Security as SecurityIcon,
  DesignServices as DesignServicesIcon,
  Code as CodeIcon,
  Construction as ConstructionIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  RocketLaunch as RocketLaunchIcon,
  SupportAgent as SupportAgentIcon,
  East as EastIcon
} from '@mui/icons-material';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import PageTitle from '../../components/PageTitle';

// Define types for our state and data structures
interface VisibilityState {
  hero: boolean;
  overview: boolean;
  features: boolean;
  process: boolean;
  technologies: boolean;
  cta: boolean;
  testimonials: boolean;
}

interface SectionRefs {
  hero: React.RefObject<HTMLElement>;
  overview: React.RefObject<HTMLElement>;
  features: React.RefObject<HTMLElement>;
  process: React.RefObject<HTMLElement>;
  technologies: React.RefObject<HTMLElement>;
  cta: React.RefObject<HTMLElement>;
  testimonials: React.RefObject<HTMLElement>;
}

interface PortfolioProject {
  id: number;
  title: string;
  description: string;
  image: string;
  tags: string[];
}

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface ProcessStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface Technology {
  name: string;
  icon: React.ReactNode;
  delay: number;
}

interface Testimonial {
  id: number;
  content: string;
  author: string;
  position: string;
  image: string;
  rating: number;
}

// Custom styled components
const HeroBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#044D76', // Updated to the requested color
  color: theme.palette.common.white,
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(16, 0),
  },
}));

const EcommerceBg = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.05,
  overflow: 'hidden',
}));

const GradientCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
}));

const FadeInBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const FeatureCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  backgroundColor: '#ffffff',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
    '& .featureIcon': {
      transform: 'scale(1.1)',
    },
    '& .featureTitle': {
      color: '#044D76', // Updated to match button color
    },
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  transition: 'transform 0.3s ease',
  marginBottom: theme.spacing(2),
}));

const TechCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: '12px',
  textAlign: 'center',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 153, 255, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
    '& .techIcon': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(4, 77, 118, 0.15)',
    }
  },
}));

const ProcessTimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  height: '100%',
  width: '2px',
  backgroundColor: 'rgba(4, 77, 118, 0.2)', // Updated to match theme
  opacity: 0.5,
}));

const ProcessStepBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const ProcessIconBox = styled(Box)(({ theme }) => ({
  zIndex: 10,
  flexShrink: 0,
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#044D76', // Updated to match button color
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  boxShadow: theme.shadows[5],
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  borderRadius: '12px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[10],
    '& .projectImage': {
      transform: 'scale(1.1)',
    },
    '& .projectOverlay': {
      opacity: 1,
    },
  },
}));

const ProjectImage = styled('img')({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
});

const ProjectOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(to top, ${theme.palette.common.black}cc, ${theme.palette.common.black}66, transparent)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: theme.spacing(3),
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const TechIconBox = styled(Box)(({ theme }) => ({
  height: 64,
  width: 64,
  borderRadius: '50%',
  backgroundColor: 'rgba(4, 77, 118, 0.1)', // Updated color
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px auto',
  transition: 'all 0.3s ease',
}));

const CodeEditor = styled(Box)(({ theme }) => ({
  backgroundColor: '#282c34',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-5px)',
  },
}));

const EditorHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#21252b',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const EditorDot = styled(Box)<{ color: string }>(({ color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8,
}));

const EditorFilename = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.85rem',
  fontFamily: 'monospace',
  marginLeft: 8,
}));

const CodeContent = styled(Box)(({ theme }) => ({
  padding: '16px',
  overflowX: 'auto',
  maxHeight: '500px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

const PreformattedCode = styled('pre')(({ theme }) => ({
  margin: 0,
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '0.85rem',
  lineHeight: 1.5,
  color: '#e6e6e6',
}));

const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.common.white,
  position: 'relative',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 30,
    left: 30,
    width: 60,
    height: 60,
    borderRadius: '50% 0 50% 50%',
    backgroundColor: 'rgba(4, 77, 118, 0.1)',
    zIndex: 0
  }
}));

const TestimonialNavButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#044D76',
  border: '2px solid rgba(4, 77, 118, 0.2)',
  zIndex: 2,
  '&:hover': {
    backgroundColor: 'rgba(4, 77, 118, 0.1)'
  }
}));

const EcommercePage: React.FC = () => {
  const theme = useTheme();
  
  const [isVisible, setIsVisible] = useState<VisibilityState>({
    hero: false,
    overview: false,
    features: false,
    process: false,
    technologies: false,
    cta: false,
    testimonials: false
  });
  
  // Initialize the refs with proper typing
  const sectionRefs: SectionRefs = {
    hero: useRef<HTMLElement>(null),
    overview: useRef<HTMLElement>(null),
    features: useRef<HTMLElement>(null),
    process: useRef<HTMLElement>(null),
    technologies: useRef<HTMLElement>(null),
    cta: useRef<HTMLElement>(null),
    testimonials: useRef<HTMLElement>(null)
  };
  
  // Track when sections come into view
  useEffect(() => {
    const observers: Record<string, IntersectionObserver> = {};
    
    Object.entries(sectionRefs).forEach(([section, ref]) => {
      observers[section] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        },
        { threshold: 0.1 }
      );
      
      if (ref.current) {
        observers[section].observe(ref.current);
      }
    });
    
    return () => {
      Object.entries(observers).forEach(([section, observer]) => {
        if (sectionRefs[section as keyof SectionRefs].current) {
          observer.unobserve(sectionRefs[section as keyof SectionRefs].current!);
        }
      });
    };
  }, []);
  
  const handleContactClick = (): void => {
    console.log('Contact us clicked');
    // Add navigation logic here
  };
  
  // Sample portfolio projects
  const portfolioProjects: PortfolioProject[] = [
    {
      id: 1,
      title: 'Fashion E-Commerce Platform',
      description: 'A comprehensive fashion retail platform with multi-vendor support, advanced product filtering, and personalized recommendations.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Fashion+Store',
      tags: ['Shopify', 'Custom Development', 'Mobile App', 'Analytics']
    },
    {
      id: 2,
      title: 'B2B Wholesale Marketplace',
      description: 'A robust B2B platform connecting manufacturers with retailers, featuring bulk ordering, custom pricing tiers, and inventory management.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=B2B+Marketplace',
      tags: ['WooCommerce', 'API Integration', 'Payment Gateway', 'CRM']
    },
    {
      id: 3,
      title: 'Subscription Box Service',
      description: 'A subscription-based e-commerce platform with recurring billing, customer portal, and advanced inventory forecasting.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Subscription+Service',
      tags: ['Custom Platform', 'Subscription Management', 'React', 'Node.js']
    }
  ];

  // Feature items
  const featureItems: FeatureItem[] = [
    {
      icon: <StoreIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "E-Commerce Platform Development",
      description: "Custom-built online stores and marketplaces optimized for your specific business needs, with seamless user experience across all devices.",
      delay: 0
    },
    {
      icon: <ShoppingCartIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Shopping Cart & Checkout",
      description: "Streamlined checkout processes with multiple payment options, order management, and secure transactions to maximize conversion rates.",
      delay: 100
    },
    {
      icon: <InventoryIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Inventory Management",
      description: "Advanced inventory tracking systems with real-time updates, low-stock alerts, and automated reordering to prevent stockouts.",
      delay: 200
    },
    {
      icon: <LocalShippingIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Shipping & Fulfillment",
      description: "Integration with multiple shipping carriers, automated shipping label generation, and order tracking to enhance the delivery experience.",
      delay: 300
    },
    {
      icon: <PaymentIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Payment Processing",
      description: "Secure payment gateway integrations supporting multiple currencies, subscription billing, and fraud prevention systems.",
      delay: 400
    },
    {
      icon: <AnalyticsIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Analytics & Reporting",
      description: "Comprehensive dashboards with sales metrics, customer behavior insights, and conversion analytics to drive data-informed decisions.",
      delay: 500
    }
  ];

  // Process steps
  const processSteps: ProcessStep[] = [
    {
      icon: <PlaylistAddCheckIcon />,
      title: "Discovery & Strategy",
      description: "We analyze your business requirements, target audience, and competition to develop a tailored e-commerce strategy that aligns with your goals.",
      delay: 0
    },
    {
      icon: <DesignServicesIcon />,
      title: "UX/UI Design",
      description: "Our designers create intuitive, conversion-focused interfaces with compelling product displays, user-friendly navigation, and mobile optimization.",
      delay: 200
    },
    {
      icon: <CodeIcon />,
      title: "Development",
      description: "We build your e-commerce platform using the most appropriate technology stack, ensuring scalability, security, and excellent performance.",
      delay: 400
    },
    {
      icon: <ConstructionIcon />,
      title: "Integration",
      description: "We integrate essential services like payment gateways, shipping providers, inventory systems, and marketing tools for a complete solution.",
      delay: 600
    },
    {
      icon: <RocketLaunchIcon />,
      title: "Launch & Marketing",
      description: "We deploy your platform and implement digital marketing strategies including SEO, content marketing, and social media to drive traffic and sales.",
      delay: 800
    },
    {
      icon: <SupportAgentIcon />,
      title: "Support & Optimization",
      description: "We provide ongoing technical support, performance monitoring, and data-driven optimizations to continuously improve your conversion rates.",
      delay: 1000
    }
  ];

  // Technologies
  const technologies: Technology[] = [
    { 
      name: "Shopify", 
      icon: <img src="https://cdn.shopify.com/s/assets/images/logos/shopify-bag.png" alt="Shopify" width="48" height="48" />,
      delay: 0 
    },
    { 
      name: "WooCommerce", 
      icon: <img src="https://woocommerce.com/wp-content/uploads/2023/10/woo-icon-concept.svg" alt="WooCommerce" width="48" height="48" />,
      delay: 100 
    },
    { 
      name: "Magento", 
      icon: <img src="https://business.adobe.com/content/dam/dx/icons/topics/svg/adobe-commerce.svg" alt="Magento" width="48" height="48" />,
      delay: 200 
    },
    { 
      name: "BigCommerce", 
      icon: <img src="https://www.bigcommerce.com/assets/images/logos/bc-logo-dark.svg" alt="BigCommerce" width="48" height="48" />,
      delay: 300 
    },
    { 
      name: "Stripe", 
      icon: <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" width="48" height="48" />,
      delay: 400 
    },
    { 
      name: "PayPal", 
      icon: <img src="https://www.paypalobjects.com/webstatic/icon/pp258.png" alt="PayPal" width="48" height="48" />,
      delay: 500 
    },
    { 
      name: "React", 
      icon: <img src="https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg" alt="React" width="48" height="48" />,
      delay: 600 
    },
    { 
      name: "Node.js", 
      icon: <img src="https://nodejs.org/static/images/logo.svg" alt="Node.js" width="48" height="48" />,
      delay: 700 
    },
    { 
      name: "AWS", 
      icon: <img src="https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png" alt="AWS" width="48" height="48" />,
      delay: 800 
    },
    { 
      name: "Google Analytics", 
      icon: <img src="https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg" alt="Google Analytics" width="48" height="48" />,
      delay: 900 
    },
    { 
      name: "Elasticsearch", 
      icon: <img src="https://www.elastic.co/static-res/images/elastic-logo-200.png" alt="Elasticsearch" width="48" height="48" />,
      delay: 1000 
    },
    { 
      name: "Redis", 
      icon: <img src="https://redis.io/images/redis-logo.svg" alt="Redis" width="48" height="48" />,
      delay: 1100 
    }
  ];

  // Testimonials
  const testimonials: Testimonial[] = [
    {
      id: 1,
      content: "Our sales increased by 135% in the first six months after launching our new e-commerce platform. The user experience is exceptional, and the admin dashboard gives us all the insights we need to make strategic decisions.",
      author: "Jennifer Martinez",
      position: "CEO, Fashion Boutique",
      image: "https://randomuser.me/api/portraits/women/12.jpg",
      rating: 5
    },
    {
      id: 2,
      content: "The team delivered our B2B marketplace ahead of schedule and within budget. The platform's performance has been rock-solid even during peak sales periods, and the automation features have saved us countless hours of manual work.",
      author: "Michael Thompson",
      position: "COO, Industrial Supplies Inc.",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      rating: 5
    },
    {
      id: 3,
      content: "Working with their team on our subscription service was a game-changer for our business model. The recurring billing system works flawlessly, and the customer portal has significantly reduced support inquiries.",
      author: "Sarah Wilson",
      position: "Founder, Monthly Box Co.",
      image: "https://randomuser.me/api/portraits/women/33.jpg",
      rating: 4
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const handlePrevTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0099ff0a' }}>
      <PageTitle title="E-Commerce" />

      <Navbar />
      <HeroBox component="section" ref={sectionRefs.hero}>
        {/* Background elements */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <GradientCircle 
            sx={{ 
              top: 0, 
              right: 0, 
              width: 800, 
              height: 800, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(25%, -25%)'
            }}
          />
          <GradientCircle 
            sx={{ 
              bottom: 0, 
              left: 0, 
              width: 600, 
              height: 600, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(-33%, 33%)'
            }}
          />
          
          {/* E-commerce Background Elements */}
          <EcommerceBg>
            <svg width="100%" height="100%" viewBox="0 0 1200 800">
              {/* Shopping Cart Icons */}
              <g opacity="0.1" fill="white">
                <path d="M200,200 L230,200 L250,280 L150,280 L130,200 Z" stroke="white" strokeWidth="2" />
                <circle cx="170" cy="300" r="15" stroke="white" strokeWidth="2" />
                <circle cx="230" cy="300" r="15" stroke="white" strokeWidth="2" />
                
                <path d="M400,200 L430,200 L450,280 L350,280 L330,200 Z" stroke="white" strokeWidth="2" />
                <circle cx="370" cy="300" r="15" stroke="white" strokeWidth="2" />
                <circle cx="430" cy="300" r="15" stroke="white" strokeWidth="2" />
                
                <path d="M600,200 L630,200 L650,280 L550,280 L530,200 Z" stroke="white" strokeWidth="2" />
                <circle cx="570" cy="300" r="15" stroke="white" strokeWidth="2" />
                <circle cx="630" cy="300" r="15" stroke="white" strokeWidth="2" />
                
                <path d="M800,200 L830,200 L850,280 L750,280 L730,200 Z" stroke="white" strokeWidth="2" />
                <circle cx="770" cy="300" r="15" stroke="white" strokeWidth="2" />
                <circle cx="830" cy="300" r="15" stroke="white" strokeWidth="2" />
              </g>
              
              {/* Product Cards */}
              <g opacity="0.1" fill="white">
                <rect x="150" y="400" width="200" height="250" rx="10" stroke="white" strokeWidth="2" />
                <rect x="170" y="420" width="160" height="120" rx="5" stroke="white" strokeWidth="2" />
                <rect x="170" y="560" width="100" height="20" rx="5" stroke="white" strokeWidth="2" />
                <rect x="170" y="590" width="140" height="10" rx="5" stroke="white" strokeWidth="2" />
                <rect x="170" y="610" width="120" height="10" rx="5" stroke="white" strokeWidth="2" />
                
                <rect x="400" y="400" width="200" height="250" rx="10" stroke="white" strokeWidth="2" />
                <rect x="420" y="420" width="160" height="120" rx="5" stroke="white" strokeWidth="2" />
                <rect x="420" y="560" width="100" height="20" rx="5" stroke="white" strokeWidth="2" />
                <rect x="420" y="590" width="140" height="10" rx="5" stroke="white" strokeWidth="2" />
                <rect x="420" y="610" width="120" height="10" rx="5" stroke="white" strokeWidth="2" />
                
                <rect x="650" y="400" width="200" height="250" rx="10" stroke="white" strokeWidth="2" />
                <rect x="670" y="420" width="160" height="120" rx="5" stroke="white" strokeWidth="2" />
                <rect x="670" y="560" width="100" height="20" rx="5" stroke="white" strokeWidth="2" />
                <rect x="670" y="590" width="140" height="10" rx="5" stroke="white" strokeWidth="2" />
                <rect x="670" y="610" width="120" height="10" rx="5" stroke="white" strokeWidth="2" />
              </g>
              
              {/* Payment Icons */}
              <g opacity="0.1" fill="white">
                <rect x="950" y="450" width="80" height="50" rx="5" stroke="white" strokeWidth="2" />
                <rect x="950" y="510" width="80" height="50" rx="5" stroke="white" strokeWidth="2" />
                <rect x="950" y="570" width="80" height="50" rx="5" stroke="white" strokeWidth="2" />
              </g>
            </svg>
          </EcommerceBg>
        </Box>

        <Container sx={{ position: 'relative', zIndex: 10 }}>
          <Box maxWidth="md">
            <FadeInBox visible={isVisible.hero}>
              <Typography variant="h2" component="h1" gutterBottom>
                E-Commerce
                <Box component="span" display="block" sx={{ color: '#0099ff' }}>Solutions</Box>
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={200}>
              <Typography variant="h5" paragraph sx={{ color: 'grey.300' }}>
                Custom online stores and marketplaces that drive sales, enhance customer experience, and scale with your business
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={400} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleContactClick}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  bgcolor: '#044D76',
                  '&:hover': { 
                    bgcolor: alpha('#044D76', 0.9),
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large" 
                href="#portfolio"
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { 
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                View Our Work
              </Button>
            </FadeInBox>
          </Box>
        </Container>

        {/* Wave shape divider */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 80.5C672 71 768 35.5 864 26.625C960 17.75 1056 35.5 1152 44.375C1248 53.25 1344 53.25 1392 53.25H1440V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z"
              fill={theme.palette.background.default}
            />
          </svg>
        </Box>
      </HeroBox>

      {/* Overview Section */}
      <Box component="section" ref={sectionRefs.overview} py={10}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview}>
                <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
                  Elevate Your Online Retail Experience
                </Typography>
                <Typography variant="body1" paragraph>
                  In today's digital marketplace, having a powerful e-commerce presence is essential for business success. Our e-commerce solutions are designed to create seamless shopping experiences that convert visitors into loyal customers.
                </Typography>
                <Typography variant="body1" paragraph>
                  Whether you're launching a new online store, scaling an existing one, or transforming into a multi-channel retailer, our team develops tailored solutions that align with your business goals and customer expectations.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                  {[
                    "Conversion-optimized shopping experiences",
                    "Seamless mobile and desktop interfaces",
                    "Scalable infrastructure for growth",
                    "Secure payment processing"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#044D76' }} />
                      <Typography variant="body1" fontWeight="medium">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </FadeInBox>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview} delay={300}>
                <Box sx={{ position: 'relative' }}>
                  {/* Improved code editor styling */}
                  <CodeEditor>
                    <EditorHeader>
                      <EditorDot color="#ff5f56" />
                      <EditorDot color="#ffbd2e" />
                      <EditorDot color="#27c93f" />
                      <EditorFilename>ProductController.js</EditorFilename>
                    </EditorHeader>
                    
                    <CodeContent>
                      <PreformattedCode>
{`import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchProductDetails, 
  addToCart,
  updateWishlist 
} from '../redux/actions';
import { 
  ProductGallery, 
  ProductDetails,
  VariantSelector,
  AddToCartButton,
  RelatedProducts,
  ProductReviews 
} from '../components';

/**
 * Product Detail Page Component
 * Displays complete information about a product
 */
const ProductPage = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const { product, loading, error } = useSelector(state => state.product);
  const { cart } = useSelector(state => state.cart);
  
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  useEffect(() => {
    // Fetch product data when component mounts
    dispatch(fetchProductDetails(productId));
    
    // Track product view for analytics
    analytics.trackProductView({
      product_id: productId,
      referrer: document.referrer
    });
  }, [productId, dispatch]);
  
  // Update selected variant when product data loads
  useEffect(() => {
    if (product && product.variants?.length > 0) {
      setSelectedVariant(product.variants[0]);
    }
  }, [product]);

  const handleAddToCart = () => {
    dispatch(addToCart({
      productId,
      variantId: selectedVariant?.id,
      quantity,
      price: selectedVariant?.price || product.price
    }));
  };
  
  // Add to wishlist handler
  const handleAddToWishlist = () => {
    dispatch(updateWishlist(productId));
  };`}
                      </PreformattedCode>
                    </CodeContent>
                  </CodeEditor>
                  
                  {/* Background decoration */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -24, 
                      right: -24, 
                      width: 128, 
                      height: 128, 
                      borderRadius: '50%', 
                      bgcolor: '#0099ff',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: -32, 
                      left: -32, 
                      width: 160, 
                      height: 160, 
                      borderRadius: '50%', 
                      bgcolor: '#044D76',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                </Box>
              </FadeInBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        component="section" 
        ref={sectionRefs.features}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.features}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our E-Commerce Services
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We offer comprehensive e-commerce solutions to help businesses sell online effectively, manage inventory efficiently, and provide exceptional shopping experiences.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {featureItems.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard 
                  elevation={2}
                  visible={isVisible.features}
                  delay={feature.delay}
                >
                  <IconBox className="featureIcon">
                    {feature.icon}
                  </IconBox>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    className="featureTitle"
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Box component="section" ref={sectionRefs.process} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.process}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our E-Commerce Implementation Process
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We follow a structured approach to developing e-commerce platforms that ensures a seamless shopping experience for your customers and efficient management for your team.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <ProcessTimelineLine />
            
            {/* Process steps */}
            {processSteps.map((step, index) => (
              <ProcessStepBox 
                key={index}
                visible={isVisible.process}
                delay={step.delay}
                sx={{ 
                  flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                }}
              >
                <Box 
                  sx={{ 
                    width: '50%',
                    textAlign: index % 2 === 0 ? 'right' : 'left',
                    pr: index % 2 === 0 ? 6 : 0,
                    pl: index % 2 === 0 ? 0 : 6,
                  }}
                >
                  <Typography variant="h6" component="h3" gutterBottom sx={{ color: '#044D76' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                
                <ProcessIconBox>
                  {step.icon}
                </ProcessIconBox>
                
                <Box sx={{ width: '50%' }} />
              </ProcessStepBox>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Technologies Section */}
      <Box 
        component="section" 
        ref={sectionRefs.technologies}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.technologies}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              E-Commerce Technologies We Use
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We leverage leading e-commerce platforms and technologies to build scalable, secure, and feature-rich online stores tailored to your business needs.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {technologies.map((tech, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <TechCard 
                  elevation={1}
                  visible={isVisible.technologies}
                  delay={tech.delay}
                >
                  <TechIconBox className="techIcon">
                    {tech.icon}
                  </TechIconBox>
                  <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                    {tech.name}
                  </Typography>
                  <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                  <Typography variant="body2" color="text.secondary">
                    Expert implementation
                  </Typography>
                </TechCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box component="section" ref={sectionRefs.testimonials} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.testimonials}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Client Success Stories
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Discover what our clients have to say about their e-commerce platforms and the results they've achieved.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative', maxWidth: 'md', mx: 'auto' }}>
            {/* Left Navigation */}
            <TestimonialNavButton 
              onClick={handlePrevTestimonial}
              sx={{ 
                left: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
            
            {/* Testimonial Card */}
            <TestimonialCard elevation={3}>
              <Typography 
                variant="body1" 
                component="blockquote" 
                sx={{ 
                  fontStyle: 'italic', 
                  position: 'relative',
                  mb: 4,
                  '&::before': {
                    content: '"\\201C"',
                    position: 'absolute',
                    left: -30,
                    top: -20,
                    fontSize: '5rem',
                    color: 'rgba(4, 77, 118, 0.1)',
                    fontFamily: 'Georgia, serif'
                  }
                }}
              >
                "{testimonials[currentTestimonial].content}"
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Box 
                    key={index}
                    component="span"
                    sx={{ 
                      color: index < testimonials[currentTestimonial].rating ? '#0099ff' : 'rgba(0, 0, 0, 0.2)',
                      mx: 0.5,
                      fontSize: '24px',
                    }}
                  >
                    ★
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box 
                  component="img"
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].author}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    mr: 2,
                    border: '2px solid rgba(4, 77, 118, 0.2)'
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: '#044D76', fontWeight: 'bold' }}>
                    {testimonials[currentTestimonial].author}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testimonials[currentTestimonial].position}
                  </Typography>
                </Box>
              </Box>
            </TestimonialCard>
            
            {/* Right Navigation */}
            <TestimonialNavButton 
              onClick={handleNextTestimonial}
              sx={{ 
                right: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
          </Box>
        </Container>
      </Box>

      {/* Portfolio Section */}
      <Box component="section" id="portfolio" py={10}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our E-Commerce Portfolio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore some of our successful e-commerce implementations and the results we've delivered for our clients.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {portfolioProjects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard elevation={4}>
                  <Box sx={{ height: 240, position: 'relative' }}>
                    <ProjectImage 
                      src={project.image} 
                      alt={project.title}
                      className="projectImage"
                    />
                    <ProjectOverlay className="projectOverlay">
                      <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'common.white' }}>
                        {project.title}
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ color: 'grey.200' }}>
                        {project.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.tags.map((tag, i) => (
                          <Chip 
                            key={i}
                            label={tag}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(4, 77, 118, 0.8)', 
                              color: 'white',
                              fontWeight: 'medium',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>
                    </ProjectOverlay>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5, 
                bgcolor: '#044D76',
                color: 'common.white',
                '&:hover': {
                  bgcolor: alpha('#044D76', 0.9),
                  transform: 'translateY(-4px)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
              }}
            >
              View All Projects
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        component="section" 
        ref={sectionRefs.cta}
        py={10}
        sx={{ 
          bgcolor: '#044D76', 
          color: 'common.white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background elements */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <GradientCircle 
            sx={{ 
              top: 0, 
              right: 0, 
              width: 600, 
              height: 600, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.3)} 0%, transparent 70%)`,
              transform: 'translate(33%, -33%)'
            }}
          />
          <GradientCircle 
            sx={{ 
              bottom: 0, 
              left: 0, 
              width: 400, 
              height: 400, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              transform: 'translate(-33%, 33%)'
            }}
          />
          
          {/* Shopping Cart Background */}
          <Box sx={{ position: 'absolute', inset: 0, opacity: 0.05 }}>
            <svg width="100%" height="100%" viewBox="0 0 1200 600">
              <g fill="none" stroke="white" strokeWidth="2">
                {/* Shopping carts */}
                {[...Array(6)].map((_, i) => (
                  <g key={i} transform={`translate(${200 * i}, ${100 + 100 * (i % 3)})`}>
                    <path d="M0,0 L30,0 L50,80 L-50,80 L-70,0 Z" />
                    <circle cx="-30" cy="100" r="15" />
                    <circle cx="30" cy="100" r="15" />
                  </g>
                ))}
                
                {/* Payment icons */}
                {[...Array(5)].map((_, i) => (
                  <rect key={i} x={900} y={100 + 80 * i} width="100" height="60" rx="10" />
                ))}
              </g>
            </svg>
          </Box>
        </Box>
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeInBox visible={isVisible.cta} sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom>
              Ready to Transform Your Online Business?
            </Typography>
            <Typography variant="h6" component="p" sx={{ mb: 6, color: alpha(theme.palette.common.white, 0.9) }}>
              Let's discuss how our e-commerce solutions can help you increase sales, streamline operations, and provide exceptional shopping experiences for your customers.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                size="large"
                onClick={handleContactClick}
                sx={{ 
                  px: 4, 
                  py: 2,
                  bgcolor: theme.palette.common.white,
                  color: '#044D76',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 20px ${alpha(theme.palette.common.black, 0.2)}`
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                color="inherit"
                size="large"
                sx={{ 
                  px: 4, 
                  py: 2,
                  borderWidth: 2,
                  fontWeight: 'bold',
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                }}
              >
                Request a Consultation
              </Button>
            </Box>
          </FadeInBox>
        </Container>
      </Box>

      {/* Project CTA Section */}
      <Box 
        component="section"
        py={8}
        sx={{ 
          bgcolor: '#212529',
          color: 'common.white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                Do you have an e-commerce project in mind?
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', maxWidth: '80%' }}>
                Let's collaborate to bring your online store vision to life. Our team of experts is
                ready to discuss your business needs and provide tailored e-commerce solutions to drive
                your growth.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 4, md: 0 } }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  bgcolor: '#044D76',
                  '&:hover': {
                    bgcolor: '#033860',
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 1,
                  fontWeight: 'bold',
                }}
                endIcon={<EastIcon />}
              >
                Let's Talk
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default EcommercePage;